import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import {
    getAllReportEntitiesAsync,
    getReportEntityAsync,
    createReportEntityAsync,
    updateReportEntityAsync,
    deleteReportEntityAsync,
    UpdateReportEntity,
    CreateReportEntity,
} from "./reportEntityApi";
import { queryKeys } from "../queryKeys";

export function useGetReportEntities() {
    return useQuery({ ...queryKeys.reportEntities.all, queryFn: getAllReportEntitiesAsync });
}

export const useGetReportEntity = (args: { reportId: string | undefined; leadEntityId: string | undefined }) => {
    const toast = useToast();
    return useQuery({
        ...queryKeys.reportEntities.byId({ reportId: args.reportId || "", leadEntityId: args.leadEntityId || "" }),
        queryFn: async () => {
            const reportEntity = await getReportEntityAsync({ reportId: args.reportId || "", leadEntityId: args.leadEntityId || "" })
                .then(reportEntity => {
                    return reportEntity;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the reportEntity information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return reportEntity;
        },
        enabled: !!args.reportId && !!args.leadEntityId,
    });
};

export function useUpdateReportEntity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args: UpdateReportEntity) => {
            return await updateReportEntityAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.reportEntities.all);
        },
    });
}

export function useCreateReportEntity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args: CreateReportEntity) => {
            return await createReportEntityAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.reportEntities.all);
        },
    });
}

export function useDeleteReportEntity() {
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { reportId: string | undefined; leadEntityId: string | undefined }) => {
            return await deleteReportEntityAsync({ reportId: args.reportId || "", leadEntityId: args.leadEntityId || "" });
        },
        onSuccess: async () => {
            toast({
                title: "Deleted",
                description: "ReportEntity deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete reportEntity"),
    });
}
